import { useEffect, useMemo, useState } from 'react';
import { CalendarHeart, HeartPulse, Hand, Palmtree, Baby, FileSpreadsheet } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { useAuth } from '@/hooks/useAuth';
import { useWorkspace } from '@/hooks/useWorkspace';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { showToast } from '@/components/ui/toaster';
import {
    hrRequestService,
    LEAVE_TYPES,
    leaveDays,
    type HrRequestType,
    type HrRequestStatus,
} from '@/services/hr-request.service';
import { fmtDate } from '@/lib/time';

const TYPE_META: Record<string, { color: string; bg: string; icon: any; labelKey: string }> = {
    sick_leave:      { color: '#b91c1c', bg: '#fee2e2', icon: HeartPulse, labelKey: 'ot.type.sick_leave' },
    permission_leave:{ color: '#a07b00', bg: '#fef9e7', icon: Hand,       labelKey: 'ot.type.permission_leave' },
    annual_leave:    { color: '#3d8b37', bg: '#e8f3e8', icon: Palmtree,   labelKey: 'ot.type.annual_leave' },
    maternity_leave: { color: '#7c3aed', bg: '#ede9fe', icon: Baby,       labelKey: 'ot.type.maternity_leave' },
};

const STATUS_STYLE: Record<HrRequestStatus, { bg: string; color: string; labelKey: string }> = {
    pending:   { bg: '#fef9e7', color: '#a07b00', labelKey: 'ot.status.pending' },
    approved:  { bg: '#e8f3e8', color: '#3d8b37', labelKey: 'ot.status.approved' },
    rejected:  { bg: '#fee2e2', color: '#b91c1c', labelKey: 'ot.status.rejected' },
    cancelled: { bg: '#f3f4f6', color: '#6b7280', labelKey: 'ot.status.cancelled' },
};

interface LeaveRow {
    id: string;
    type: HrRequestType;
    fromAt: string;
    toAt?: string | null;
    status: HrRequestStatus;
    payload?: { reason?: string } | null;
}

interface AnnualBalance {
    quota: number;
    used: number;
    remaining: number;
}

export default function LeaveHistoryPage() {
    const { t } = useI18n();
    const { user } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const [rows, setRows] = useState<LeaveRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState<HrRequestType | ''>('');
    const [filterStatus, setFilterStatus] = useState<HrRequestStatus | ''>('');
    const [annual, setAnnual] = useState<AnnualBalance | null>(null);

    useEffect(() => {
        if (!currentWorkspace) return;
        load();
        loadAnnualBalance();
    }, [currentWorkspace?._id]);

    const load = async () => {
        if (!currentWorkspace) return;
        setLoading(true);
        try {
            // Ambil seluruh tipe cuti milik sendiri sekaligus, gabung & urut terbaru dulu.
            const results = await Promise.all(
                LEAVE_TYPES.map((type) =>
                    hrRequestService
                        .list({ workspaceId: currentWorkspace._id, scope: 'mine', type })
                        .then((res) => (res.data as LeaveRow[]) || [])
                        .catch(() => [] as LeaveRow[]),
                ),
            );
            const merged = results
                .flat()
                .sort((a, b) => new Date(b.fromAt).getTime() - new Date(a.fromAt).getTime());
            setRows(merged);
        } catch (e: any) {
            showToast({ variant: 'destructive', description: e?.response?.data?.error || t('common.save_failed') });
        } finally {
            setLoading(false);
        }
    };

    // Kuota tahunan hanya bisa dibaca pegawai yang punya visibilitas tim (endpoint admin/team-only).
    // Untuk pegawai biasa endpoint 403 → diam-diam dilewati, kartu kuota tak ditampilkan.
    const loadAnnualBalance = async () => {
        if (!currentWorkspace || !user?._id) return;
        try {
            const year = new Date().getFullYear();
            const res = await hrRequestService.leaveRecap(currentWorkspace._id, {
                from: new Date(year, 0, 1).toISOString(),
                to: new Date(year, 11, 31, 23, 59, 59).toISOString(),
                status: 'approved',
            });
            // Endpoint balas { data: {...} }; interceptor PWA cuma unwrap {success,data},
            // jadi harus baca res.data.data (bukan res.data langsung).
            const employees = ((res.data as any)?.data?.employees as any[]) || [];
            const me = employees.find((e) => e.userId === user._id);
            if (me && typeof me.annualQuota === 'number') {
                setAnnual({
                    quota: me.annualQuota || 0,
                    used: me.annualUsed || 0,
                    remaining: me.annualRemaining ?? Math.max(0, (me.annualQuota || 0) - (me.annualUsed || 0)),
                });
            }
        } catch {
            // pegawai tanpa akses rekap — tak apa, sembunyikan kartu kuota
        }
    };

    // Rekap per tipe (tahun berjalan, hanya disetujui) untuk kartu ringkasan.
    const summary = useMemo(() => {
        const year = new Date().getFullYear();
        const agg: Record<string, number> = { sick_leave: 0, permission_leave: 0, annual_leave: 0, maternity_leave: 0 };
        for (const r of rows) {
            if (r.status !== 'approved') continue;
            if (new Date(r.fromAt).getFullYear() !== year) continue;
            if (agg[r.type] === undefined) continue;
            agg[r.type] += leaveDays(r.fromAt, r.toAt);
        }
        return agg;
    }, [rows]);

    const visibleRows = useMemo(
        () =>
            rows.filter(
                (r) =>
                    (!filterType || r.type === filterType) &&
                    (!filterStatus || r.status === filterStatus),
            ),
        [rows, filterType, filterStatus],
    );

    if (!currentWorkspace) {
        return <div className="px-4 pt-12 max-w-md mx-auto text-center text-sm text-gray-500">{t('common.loading')}</div>;
    }

    return (
        <div className="px-4 pt-3 max-w-md mx-auto space-y-3">
            <header className="pt-2 mb-1">
                <div className="flex items-center gap-2">
                    <div className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#e8f3e8' }}>
                        <CalendarHeart className="h-5 w-5" style={{ color: '#3d8b37' }} />
                    </div>
                    <div>
                        <h1 className="text-lg font-black text-gray-900">{t('leave.title')}</h1>
                        <p className="text-[11px] text-gray-500 max-w-[240px] leading-tight">{t('leave.subtitle')}</p>
                    </div>
                </div>
            </header>

            {annual && (
                <div className="rounded-2xl p-4 text-white" style={{ background: 'linear-gradient(135deg, #3d8b37, #2f6e2a)' }}>
                    <p className="text-[11px] font-bold uppercase tracking-wide opacity-80">{t('leave.annual_quota')}</p>
                    <div className="flex items-end gap-1 mt-1">
                        <span className="text-3xl font-black tabular-nums">{annual.remaining}</span>
                        <span className="text-sm font-medium opacity-80 mb-1">/ {annual.quota} {t('leave.days')}</span>
                    </div>
                    <p className="text-[11px] opacity-80 mt-0.5">{t('leave.annual_used', { used: annual.used })}</p>
                </div>
            )}

            <div className="grid grid-cols-2 gap-2">
                {LEAVE_TYPES.map((type) => {
                    const m = TYPE_META[type];
                    const Icon = m.icon;
                    return (
                        <div key={type} className="bg-white rounded-2xl p-3" style={{ border: '1px solid #e0d6cc' }}>
                            <div className="flex items-center justify-between mb-1.5">
                                <div className="h-7 w-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: m.bg }}>
                                    <Icon className="h-3.5 w-3.5" style={{ color: m.color }} />
                                </div>
                            </div>
                            <p className="text-[11px] text-gray-500 leading-tight">{t(m.labelKey)}</p>
                            <p className="text-lg font-black text-gray-900 tabular-nums">
                                {summary[type]} <span className="text-[11px] font-medium text-gray-400">{t('leave.days')}</span>
                            </p>
                        </div>
                    );
                })}
            </div>

            <p className="text-[10px] text-gray-400 px-1 -mt-1">{t('leave.summary_hint', { year: new Date().getFullYear() })}</p>

            <div className="grid grid-cols-2 gap-2">
                <Select value={filterType || 'all'} onValueChange={(v) => setFilterType((v === 'all' ? '' : v) as any)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('leave.filter_all_types')}</SelectItem>
                        {LEAVE_TYPES.map((type) => <SelectItem key={type} value={type}>{t(TYPE_META[type].labelKey)}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus((v === 'all' ? '' : v) as any)}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('leave.filter_all_status')}</SelectItem>
                        {Object.entries(STATUS_STYLE).map(([k, v]) => <SelectItem key={k} value={k}>{t(v.labelKey)}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            {loading && <p className="text-sm text-gray-400 italic px-1">{t('common.loading')}</p>}

            {!loading && visibleRows.length === 0 && (
                <div className="bg-white rounded-2xl p-8 text-center" style={{ border: '1px dashed #e0d6cc' }}>
                    <div className="h-12 w-12 mx-auto rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: '#e8f3e8' }}>
                        <FileSpreadsheet className="h-5 w-5" style={{ color: '#3d8b37' }} />
                    </div>
                    <p className="text-sm text-gray-500 max-w-[260px] mx-auto">{t('leave.empty')}</p>
                </div>
            )}

            <div className="space-y-2">
                {visibleRows.map((r) => {
                    const m = TYPE_META[r.type] || TYPE_META.annual_leave;
                    const Icon = m.icon;
                    const days = leaveDays(r.fromAt, r.toAt);
                    return (
                        <div
                            key={r.id}
                            className="w-full text-left bg-white rounded-xl p-3.5 flex items-start gap-3"
                            style={{ border: '1px solid #e0d6cc' }}
                        >
                            <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: m.bg }}>
                                <Icon className="h-4 w-4" style={{ color: m.color }} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-[11px] font-bold text-gray-700">{t(m.labelKey)}</span>
                                    <StatusPill status={r.status} />
                                </div>
                                <p className="text-[11px] text-gray-500">
                                    {fmtDate(r.fromAt)}{r.toAt ? ` – ${fmtDate(r.toAt)}` : ''}
                                    <span className="text-gray-400"> · {t('leave.days_count', { n: days })}</span>
                                </p>
                                {r.payload?.reason && (
                                    <p className="text-sm font-medium text-gray-900 mt-0.5 line-clamp-2">{r.payload.reason}</p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function StatusPill({ status }: { status: HrRequestStatus }) {
    const { t } = useI18n();
    const s = STATUS_STYLE[status];
    return (
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: s.bg, color: s.color }}>
            {t(s.labelKey)}
        </span>
    );
}
